import {
  IMPORT_PHASES,
  type ImportPhaseName,
} from "@dxf-ai-editor/drawing-model";

import { useT } from "../i18n";
import { useEditorStore } from "../state/editorStore";

/**
 * AutoCAD-style "Processing Objects" modal shown during DXF import.
 *
 * The dialog is driven entirely by `state.importProgress` in the
 * editor store. It mounts when `active === true` and unmounts as soon
 * as `clearImportProgress()` flips the flag back to false — so the
 * parent `<App />` can render it unconditionally without worrying
 * about the render-in-dom-on-demand pattern.
 *
 * Visual structure mirrors AutoCAD:
 *   ┌─────────────────────────────────────────┐
 *   │ Обработка объектов                      │
 *   │                                         │
 *   │ Парсинг объектов                        │
 *   │ ▓▓▓▓▓▓▓▓░░░░░░░░░░░░ 55%                │
 *   │ 2480 / 4500                             │
 *   └─────────────────────────────────────────┘
 *
 * Interaction: non-dismissable (no close button, no Escape handler).
 * AutoCAD's dialog blocks further UI input while the drawing is being
 * processed; we mirror that by rendering a backdrop with
 * `pointer-events: all`.
 */
export function ImportProgressDialog() {
  const t = useT();
  const importProgress = useEditorStore((store) => store.importProgress);
  const abortDxfImport = useEditorStore((store) => store.abortDxfImport);

  if (!importProgress.active || !importProgress.phase) {
    return null;
  }

  const phase: ImportPhaseName = importProgress.phase;
  const phaseLabel = t(IMPORT_PHASES[phase].labelKey);
  const percentInt = Math.floor(importProgress.percent);
  const itemsLabel = t("dxfio.progress.items_fmt", {
    processed: importProgress.itemsProcessed,
    total: importProgress.itemsTotal,
  });

  return (
    <div
      className="import-progress-backdrop"
      data-testid="import-progress-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="import-progress-title"
    >
      <div className="import-progress-card">
        <div className="import-progress-title" id="import-progress-title">
          {t("dxfio.progress.title")}
        </div>
        <div className="import-progress-phase-label">{phaseLabel}</div>
        <div className="import-progress-bar" data-testid="import-progress-bar">
          <div
            className="import-progress-bar-fill"
            style={{ width: `${percentInt}%` }}
            data-testid="import-progress-bar-fill"
            aria-valuenow={percentInt}
            aria-valuemin={0}
            aria-valuemax={100}
            role="progressbar"
          />
        </div>
        <div className="import-progress-meta">
          <span
            className="import-progress-percent"
            data-testid="import-progress-percent"
          >
            {percentInt}%
          </span>
          <span
            className="import-progress-items"
            data-testid="import-progress-items"
          >
            {itemsLabel}
          </span>
        </div>
        <div className="import-progress-actions">
          <button
            type="button"
            className="import-progress-cancel"
            data-testid="import-progress-cancel"
            onClick={() => {
              abortDxfImport();
            }}
          >
            {t("dxfio.progress.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}
